/**
 * Reference data shared across CRM screens.
 *
 * Context, not a store: lookups change rarely, the layout route loads them once,
 * and every screen below reads them synchronously. This is the whole of the
 * CRM's global state — React alone suffices because there is no
 * cache-invalidation problem to solve.
 */

import { createContext, useContext, type ReactNode } from 'react'
import type { LeadStatus } from '@erp/contracts'
import type { DealStatus } from '@erp/contracts'
import type { Lookups, LookupItem } from '@erp/contracts'
import type { CrmUser } from '@erp/contracts'

const LookupContext = createContext<Lookups | null>(null)

export function LookupProvider({ value, children }: { value: Lookups; children: ReactNode }) {
  return <LookupContext.Provider value={value}>{children}</LookupContext.Provider>
}

function useLookups(): Lookups {
  const value = useContext(LookupContext)
  if (!value) throw new Error('useLookups must be used inside the CRM layout route')
  return value
}

export function useStatus(statusId: string): LeadStatus | undefined {
  return useLookups().leadStatuses.find((status) => status.id === statusId)
}

export function useLeadStatuses(): readonly LeadStatus[] {
  return useLookups().leadStatuses
}

export function useDealStatus(statusId: string): DealStatus | undefined {
  return useLookups().dealStatuses.find((status) => status.id === statusId)
}

export function useDealStatuses(): readonly DealStatus[] {
  return useLookups().dealStatuses
}

/** Shared by leads and deals — loss reporting spans both halves of the pipeline. */
export function useLostReasons(): readonly LookupItem[] {
  return useLookups().lostReasons
}

export function useLeadSources(): readonly LookupItem[] {
  return useLookups().leadSources
}

export function useIndustries(): readonly LookupItem[] {
  return useLookups().industries
}

/** The host app's directory, bundled into the lookups payload. */
export function useUsers(): readonly CrmUser[] {
  return useLookups().users
}

export function useUser(userId: string | null): CrmUser | undefined {
  const users = useUsers()
  return userId === null ? undefined : users.find((user) => user.id === userId)
}

/**
 * Options for an owner picker. Inactive users are hidden unless one is already
 * selected — a record must not silently lose its owner because that person left.
 */
export function useOwnerOptions(selected: string | null): { value: string; label: string }[] {
  const users = useUsers()
  const selectable = users.filter((user) => user.active || user.id === selected)
  return [
    { value: '', label: 'Unassigned' },
    ...selectable.map((user) => ({
      value: user.id,
      label: user.active ? user.name : `${user.name} (inactive)`,
    })),
  ]
}

/** Options for an optional <Select>, with a leading "none" entry. */
export function optionsFrom(
  items: readonly { id: string; name: string }[],
  placeholder = 'None',
): { value: string; label: string }[] {
  return [{ value: '', label: placeholder }, ...items.map((i) => ({ value: i.id, label: i.name }))]
}
